import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';

const AuthContext = createContext(null);
const TOKEN_KEY = 'ikas_access_token';
// sessionStorage instead of localStorage: token is cleared when the tab is closed,
// which materially reduces the XSS attack window vs. a persistent token in localStorage.
const tokenStorage = () => (typeof window !== 'undefined' ? window.sessionStorage : null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = checking, null = anon, object = logged in
    const [loading, setLoading] = useState(true);

    // Attach Authorization header if token in sessionStorage (fallback if cookies fail cross-site)
    useEffect(() => {
        const store = tokenStorage();
        const tok = store?.getItem(TOKEN_KEY);
        if (tok) api.defaults.headers.common['Authorization'] = `Bearer ${tok}`;
    }, []);

    const check = useCallback(async () => {
        try {
            const { data } = await api.get('/auth/me', { withCredentials: true });
            setUser(data);
        } catch (err) {
            // 401 on /auth/me is expected for anonymous visitors — don't spam console.
            if (err?.response?.status && err.response.status !== 401) {
                console.error('Auth check failed:', err);
            }
            setUser(null);
            tokenStorage()?.removeItem(TOKEN_KEY);
            delete api.defaults.headers.common['Authorization'];
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { check(); }, [check]);

    const login = async (email, password) => {
        const { data } = await api.post('/auth/login', { email, password }, { withCredentials: true });
        if (data.access_token) {
            tokenStorage()?.setItem(TOKEN_KEY, data.access_token);
            api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
        }
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try {
            await api.post('/auth/logout', {}, { withCredentials: true });
        } catch (err) {
            console.warn('Logout API call failed (proceeding to clear local session):', err?.message || err);
        }
        tokenStorage()?.removeItem(TOKEN_KEY);
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, refresh: check }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);

export function formatApiErrorDetail(detail) {
    if (detail == null) return 'Something went wrong. Please try again.';
    if (typeof detail === 'string') {
        // Guard against HTML error pages (e.g. Cloudflare 502/504) being rendered
        // verbatim in the UI when the backend origin is unreachable.
        const looksLikeHtml = /<\w+[\s>]|<!DOCTYPE|Cloudflare/i.test(detail);
        if (looksLikeHtml || detail.length > 300) {
            return 'Service is temporarily unavailable. Please try again in a moment.';
        }
        return detail;
    }
    if (Array.isArray(detail))
        return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
}
